import Chevron from "@/icons/chevron.svg";
import {
	Description,
	Field,
	Label,
	Listbox,
	ListboxButton,
	ListboxOption,
	ListboxOptions,
	Transition,
} from "@headlessui/react";
import { ErrorMessage, FieldValuesFromFieldErrors } from "@hookform/error-message";
import { Control, FieldErrors, FieldName, FieldValues, Path, useController } from "react-hook-form";

type DropdownProps<T extends FieldValues> = {
	label: string;
	formKey: string;
	required?: boolean;
	options: string[];
	control: Control<T>;
	helpText?: string;
};

const Dropdown = <T extends FieldValues>({
	label,
	formKey,
	required,
	options,
	control,
	helpText,
}: DropdownProps<T>) => {
	const {
		field: { value, onChange },
		formState: { errors },
	} = useController({
		name: formKey as Path<T>,
		control,
		rules: { required: { value: required || false, message: "This field is required" } },
	});

	return (
		<Field as="div" className="flex flex-col">
			<Label className="form-label" htmlFor={formKey}>
				{label}
				{required && "*"}
			</Label>
			<Listbox value={value} onChange={onChange} as="div" className="relative">
				<ListboxButton className="form-input flex w-full items-center justify-between">
					<span className="truncate">{options.find((option) => option === value) || "Please select"}</span>
					<Chevron className="text-cyan ui-open:-rotate-90 h-3 w-3 rotate-90 transition-transform" />
				</ListboxButton>
				<Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
					<ListboxOptions className="form-input absolute z-10 mt-3 w-full bg-white">
						{options.map((option) => (
							<ListboxOption
								key={option}
								value={option}
								className="cursor-pointer border-b border-[#BDD4E9] py-2 underline-offset-[6px] last:border-0 hover:underline"
							>
								{option}
							</ListboxOption>
						))}
					</ListboxOptions>
				</Transition>
			</Listbox>
			{helpText && <Description className="form-help mt-2">{helpText}</Description>}
			<ErrorMessage
				name={formKey as FieldName<FieldValuesFromFieldErrors<FieldErrors<T>>>}
				errors={errors}
				render={({ message }) => <p className="mt-2 text-red-500">{message}</p>}
			/>
		</Field>
	);
};

export default Dropdown;
